import { upperCase, head, trim } from "lodash";

/**
 * Build a privacy-preserving patient name for outbound, external-recipient
 * emails: the first name is reduced to a single uppercase initial while the
 * last name is kept in full (e.g. "Zerong", "Ma" -> "Z. Ma").
 *
 * This intentionally avoids exposing the patient's full given name to referrers,
 * GP clinics and rural hospitals. Null/empty-safe: a missing first name yields
 * just the last name, and a missing last name yields just the initial.
 *
 * @param {string} firstName - Patient first name
 * @param {string} lastName - Patient last name
 * @returns {string} Masked patient name
 */
const maskPatientName = (firstName, lastName) => {
  const firstNameInitial = upperCase(head(trim(firstName)));
  return [firstNameInitial ? `${firstNameInitial}.` : "", trim(lastName)].filter(Boolean).join(" ");
};

/**
 * Mask an already-joined full name string (e.g. "Zerong Ma" -> "Z. Ma") for
 * outbound, external-recipient emails where only the combined name is available.
 * The first whitespace-separated token is treated as the first name and the
 * remainder as the last name. Null/empty-safe.
 *
 * @param {string} fullName - Patient full name ("first last")
 * @returns {string} Masked patient name
 */
const maskPatientFullName = (fullName) => {
  const [firstName = "", ...rest] = trim(fullName).split(/\s+/);
  return maskPatientName(firstName, rest.join(" "));
};

/**
 * Replace any occurrence of the patient's full name inside a free-text string
 * (e.g. an email subject or body) with the masked form. Used where the name is
 * embedded in caller-supplied text rather than a structured field. Null/empty
 * and type-safe: non-string input or a missing name returns the input unchanged.
 *
 * @param {string} text - Text that may contain the patient's full name
 * @param {string} firstName - Patient first name
 * @param {string} lastName - Patient last name
 * @returns {string} Text with the full name replaced by the masked name
 */
const maskPatientNameInText = (text, firstName, lastName) => {
  if (!text || typeof text !== "string") {
    return text;
  }
  const fullName = [trim(firstName), trim(lastName)].filter(Boolean).join(" ");
  if (!fullName) {
    return text;
  }
  return text.split(fullName).join(maskPatientName(firstName, lastName));
};

/**
 * Determine whether an email `to` value targets at least one external recipient
 * (anyone other than the patient themselves or the treating practitioner). The
 * `to` value may be a single address or a comma-separated list. Comparison is
 * case-insensitive and whitespace-trimmed.
 *
 * @param {string} to - Recipient address(es), possibly comma-separated
 * @param {string[]} internalEmails - Allow-list of internal addresses (patient, practitioner)
 * @returns {boolean} True if any recipient is not in the internal allow-list
 */
const isExternalEmailRecipient = (to, internalEmails = []) => {
  const recipients = String(to || "")
    .split(",")
    .map((email) => trim(email).toLowerCase())
    .filter(Boolean);
  if (recipients.length === 0) {
    return false;
  }
  const internal = internalEmails.map((email) => trim(email || "").toLowerCase()).filter(Boolean);
  return recipients.some((recipient) => !internal.includes(recipient));
};

export { maskPatientName, maskPatientFullName, maskPatientNameInText, isExternalEmailRecipient };
